import React, { createContext, useCallback, useContext, useState } from "react";
import { modalBoxNarrow } from "../utils/modalLayout";

const DialogContext = createContext(null);

export const useAppDialog = () => {
  const ctx = useContext(DialogContext);
  if (!ctx) {
    throw new Error("useAppDialog must be used within DialogProvider");
  }
  return ctx;
};

/**
 * Diálogos globales reemplazo de window.alert / window.confirm (UX-06 / UX-22).
 * alertDialog: mensaje con un botón Aceptar.
 * confirmDialog: devuelve Promise<boolean>.
 * options.content: nodo React opcional que se muestra debajo del mensaje
 *   (p.ej. una tabla); options.wide: ancho mayor para ese contenido.
 */
export const DialogProvider = ({ children }) => {
  const [state, setState] = useState(null);

  const alertDialog = useCallback((message, options = {}) => {
    return new Promise((resolve) => {
      setState({
        kind: "alert",
        message: String(message ?? ""),
        content: options.content || null,
        wide: Boolean(options.wide),
        title: options.title || "Aviso",
        variant: options.variant || "info",
        resolve,
      });
    });
  }, []);

  const confirmDialog = useCallback((message, options = {}) => {
    return new Promise((resolve) => {
      setState({
        kind: "confirm",
        message: String(message ?? ""),
        content: options.content || null,
        wide: Boolean(options.wide),
        title: options.title || "¿Confirmar?",
        confirmLabel: options.confirmLabel || "Confirmar",
        cancelLabel: options.cancelLabel || "Cancelar",
        danger: Boolean(options.danger),
        resolve,
      });
    });
  }, []);

  const close = (result) => {
    if (state?.resolve) state.resolve(result);
    setState(null);
  };

  return (
    <DialogContext.Provider value={{ alertDialog, confirmDialog }}>
      {children}
      {state && (
        <div
          style={overlayStyle}
          role="presentation"
          onClick={() => (state.kind === "alert" ? close(undefined) : close(false))}
        >
          <div
            style={{ ...modalShell, ...modalBoxNarrow, ...(state.wide ? { maxWidth: 640, width: "100%" } : null) }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="app-dialog-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="app-dialog-title" style={titleStyle}>
              {state.title}
            </h2>
            <div style={bodyStyle}>
              {state.message}
              {state.content && <div style={contentStyle}>{state.content}</div>}
            </div>
            <div style={footerStyle}>
              {state.kind === "confirm" && (
                <button type="button" style={btnSecondary} onClick={() => close(false)}>
                  {state.cancelLabel}
                </button>
              )}
              <button
                type="button"
                style={state.kind === "confirm" && state.danger ? btnDanger : btnPrimary}
                onClick={() => close(state.kind === "confirm" ? true : undefined)}
              >
                {state.kind === "confirm" ? state.confirmLabel : "Aceptar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </DialogContext.Provider>
  );
};

const overlayStyle = {
  position: "fixed",
  inset: 0,
  background: "rgba(15, 23, 42, 0.45)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 11000,
  padding: 16,
};

const modalShell = {
  background: "#fff",
  borderRadius: 14,
  boxShadow: "0 20px 50px rgba(0,0,0,0.18)",
  padding: "22px 22px 18px",
  fontFamily: "'FT Habit', -apple-system, BlinkMacSystemFont, sans-serif",
};

const titleStyle = {
  margin: "0 0 12px 0",
  fontSize: 17,
  fontWeight: 400,
  color: "#0f172a",
};

const bodyStyle = {
  fontSize: 14,
  color: "#334155",
  lineHeight: 1.55,
  whiteSpace: "pre-wrap",
  marginBottom: 18,
  maxHeight: "55vh",
  overflowY: "auto",
};

// Contenido rico (tablas): sin pre-wrap para que no herede los saltos de línea.
const contentStyle = {
  whiteSpace: "normal",
  marginTop: 12,
};

const footerStyle = {
  display: "flex",
  justifyContent: "flex-end",
  gap: 10,
  flexWrap: "wrap",
};

const btnPrimary = {
  background: "#0080ff",
  color: "#fff",
  border: "none",
  borderRadius: 10,
  padding: "10px 18px",
  fontWeight: 400,
  fontSize: 14,
  cursor: "pointer",
};

const btnDanger = {
  ...btnPrimary,
  background: "#dc2626",
};

const btnSecondary = {
  background: "#f1f5f9",
  color: "#475569",
  border: "1px solid #e2e8f0",
  borderRadius: 10,
  padding: "10px 18px",
  fontWeight: 400,
  fontSize: 14,
  cursor: "pointer",
};
